import { useEffect, useState } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    message: ''
  });
  const [formStatus, setFormStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      const sections = ['hizmetler', 'kalite', 'referanslar', 'iletisim'];
      const current = sections.find(section => {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          return rect.top <= 100 && rect.bottom >= 100;
        }
        return false;
      });
      if (current) setActiveSection(current);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.name || !formData.phone || !formData.message) {
      alert('Lütfen tüm alanları doldurun');
      return;
    }

    setFormStatus('sending');

    try {
      const formBody = new URLSearchParams({
        name: formData.name,
        phone: formData.phone,
        message: formData.message
      }).toString();

      const response = await fetch('https://readdy.ai/api/form/d54gac545eoa1ne7lhk0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody
      });

      if (response.ok) {
        setFormStatus('success');
        setFormData({ name: '', phone: '', message: '' });
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 3000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/908503073704"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 w-16 h-16 bg-[#25D366] rounded-full flex items-center justify-center shadow-2xl hover:scale-110 transition-all duration-300 animate-bounce cursor-pointer group"
        aria-label="WhatsApp ile iletişime geç"
      >
        <i className="ri-whatsapp-line text-white text-3xl group-hover:scale-110 transition-transform"></i>
        <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#DC2626] rounded-full animate-ping"></span>
        <span className="absolute -top-2 -right-2 w-5 h-5 bg-[#DC2626] rounded-full"></span>
      </a>

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="text-2xl font-bold bg-gradient-to-r from-[#DC2626] to-[#EF4444] bg-clip-text text-transparent">
            Yıldırım Reklam
          </div>
          
          <div className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => scrollToSection('hizmetler')}
              className={`text-sm font-medium transition-colors ${
                activeSection === 'hizmetler' ? 'text-[#DC2626]' : isScrolled ? 'text-gray-700 hover:text-[#DC2626]' : 'text-white hover:text-[#EF4444]'
              }`}
            >
              Hizmetler
            </button>
            <button 
              onClick={() => scrollToSection('kalite')}
              className={`text-sm font-medium transition-colors ${
                activeSection === 'kalite' ? 'text-[#DC2626]' : isScrolled ? 'text-gray-700 hover:text-[#DC2626]' : 'text-white hover:text-[#EF4444]'
              }`}
            >
              Kalite
            </button>
            <button 
              onClick={() => scrollToSection('referanslar')}
              className={`text-sm font-medium transition-colors ${
                activeSection === 'referanslar' ? 'text-[#DC2626]' : isScrolled ? 'text-gray-700 hover:text-[#DC2626]' : 'text-white hover:text-[#EF4444]'
              }`}
            >
              Referanslar
            </button>
            <button 
              onClick={() => scrollToSection('iletisim')}
              className={`text-sm font-medium transition-colors ${
                activeSection === 'iletisim' ? 'text-[#DC2626]' : isScrolled ? 'text-gray-700 hover:text-[#DC2626]' : 'text-white hover:text-[#EF4444]'
              }`}
            >
              İletişim
            </button>
          </div>

          <div className="flex items-center gap-4">
            <a href="tel:03123808099" className={`hidden lg:block text-sm font-semibold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>
              0312 380 80 99
            </a>
            <a 
              href="tel:03123808099"
              className="bg-gradient-to-r from-[#DC2626] to-[#EF4444] text-white px-6 py-2.5 rounded-full text-sm font-medium hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
            >
              Hemen Ara
            </a>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/935917d68377d39401d069b829427446.jpg"
            alt="Profesyonel Branda Montajı"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 w-full">
          <div className="max-w-3xl">
            <div className="inline-block bg-[#DC2626]/20 backdrop-blur-sm border border-[#DC2626]/30 px-4 py-2 rounded-full mb-6">
              <span className="text-white text-sm font-medium">Ankara'nın Güvenilir Reklam Çözümü</span>
            </div>
            
            <h1 className="text-6xl lg:text-7xl font-bold text-white leading-tight mb-6">
              Markanızı<br />
              Her Yere<br />
              Taşıyan Brandalar 🎯
            </h1>
            
            <p className="text-xl text-white/90 mb-10 leading-relaxed">
              Dijital Baskı dayanıklı, yüksek çözünürlüklü branda baskı ve montaj hizmetleriyle işletmenizi öne çıkarın
            </p>

            <div className="flex flex-wrap gap-4">
              <button 
                onClick={() => scrollToSection('iletisim')}
                className="bg-gradient-to-r from-[#DC2626] to-[#EF4444] text-white px-8 py-4 rounded-full text-base font-semibold hover:shadow-2xl hover:scale-105 transition-all whitespace-nowrap cursor-pointer"
              >
                Teklif Alın →
              </button>
              <button 
                onClick={() => scrollToSection('referanslar')}
                className="border-2 border-white text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-white hover:text-gray-900 transition-all whitespace-nowrap cursor-pointer"
              >
                Çalışmalarımız
              </button>
            </div>
          </div>

          <div className="absolute bottom-20 right-10 hidden xl:flex flex-col gap-4">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 px-6 py-4 rounded-2xl shadow-2xl">
              <div className="text-4xl font-bold text-[#DC2626] mb-1">15+</div>
              <div className="text-white text-sm">Yıl Tecrübe</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md border border-white/20 px-6 py-4 rounded-2xl shadow-2xl">
              <div className="text-4xl font-bold text-[#DC2626] mb-1">5000+</div>
              <div className="text-white text-sm">Mutlu Müşteri</div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="hizmetler" className="py-24 bg-[#F5F5F0]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col lg:flex-row justify-between items-start mb-16 gap-8">
            <div>
              <div className="text-[#DC2626] text-sm font-semibold uppercase tracking-wider mb-3">
                Hizmetlerimiz
              </div>
              <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                Her Ölçekte<br />
                Profesyonel Çözümler
              </h2>
            </div>
            <div className="lg:w-2/5">
              <p className="text-lg text-gray-600 leading-relaxed">
                Branda baskıdan dijital reklam panolarına, araç giydirmeden cephe kaplama sistemlerine kadar tüm reklam ihtiyaçlarınız için tek adres.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Service Card 1 */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer group">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=large%20format%20banner%20printing%20machine%20printing%20vibrant%20colorful%20outdoor%20advertising%20banner%20material%2C%20professional%20textile%20printer%20with%20orange%20and%20red%20banner%20fabric%2C%20clean%20white%20background%2C%20commercial%20printing%20process%2C%20high%20quality%20print%20output%2C%20detailed%20close-up%20view%20of%20printing%20technology&width=800&height=640&seq=service-banner-1&orientation=landscape"
                  alt="Branda Baskı"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-[#D4FF00] text-gray-900 px-4 py-1.5 rounded-full text-xs font-bold uppercase">
                    Popüler
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Branda Baskı & Montaj
                </h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  UV dayanıklı, yüksek çözünürlüklü branda baskı hizmetimizle dış mekan reklamlarınız yıllarca canlı kalır. Profesyonel montaj dahil.
                </p>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <i className="ri-flashlight-line text-[#DC2626]"></i>
                    <span>Hızlı</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-shield-check-line text-[#DC2626]"></i>
                    <span>Garantili</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-star-line text-[#DC2626]"></i>
                    <span>Kaliteli</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Service Card 2 */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer group">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=modern%20digital%20printing%20equipment%20with%20vibrant%20neon%20orange%20graphics%20being%20printed%20on%20large%20format%20printer%2C%20high-tech%20digital%20print%20technology%2C%20colorful%20print%20samples%2C%20professional%20commercial%20printing%20studio%20with%20clean%20white%20background%2C%20advanced%20printing%20machinery%20in%20action&width=800&height=640&seq=service-digital-1&orientation=landscape"
                  alt="Dijital Baskı"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-[#D4FF00] text-gray-900 px-4 py-1.5 rounded-full text-xs font-bold uppercase">
                    Yeni Teknoloji
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Dijital Baskı Sistemleri
                </h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Son teknoloji dijital baskı makinelerimizle afiş, poster, katalog ve tüm tanıtım materyallerinizi mükemmel kalitede üretiyoruz.
                </p>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <i className="ri-flashlight-line text-[#DC2626]"></i>
                    <span>Hızlı</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-shield-check-line text-[#DC2626]"></i>
                    <span>Garantili</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-star-line text-[#DC2626]"></i>
                    <span>Kaliteli</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Service Card 3 */}
            <div className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 cursor-pointer group">
              <div className="relative h-64 overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=professional%20vehicle%20wrap%20installation%20on%20commercial%20van%20with%20vibrant%20orange%20and%20red%20advertising%20graphics%2C%20car%20wrapping%20service%20in%20progress%2C%20automotive%20branding%20application%2C%20skilled%20technician%20applying%20vinyl%20wrap%20to%20vehicle%2C%20clean%20workshop%20environment%20with%20white%20background&width=800&height=640&seq=service-vehicle-1&orientation=landscape"
                  alt="Araç Giydirme"
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute top-4 left-4">
                  <span className="bg-[#D4FF00] text-gray-900 px-4 py-1.5 rounded-full text-xs font-bold uppercase">
                    Özel Tasarım
                  </span>
                </div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-3">
                  Araç & Cephe Kaplama
                </h3>
                <p className="text-gray-600 mb-4 leading-relaxed">
                  Araçlarınızı ve işyeri cephelerinizi mobil reklam aracına dönüştürüyoruz. Özel tasarım ve profesyonel uygulama garantisi.
                </p>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <i className="ri-flashlight-line text-[#DC2626]"></i>
                    <span>Hızlı</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-shield-check-line text-[#DC2626]"></i>
                    <span>Garantili</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-star-line text-[#DC2626]"></i>
                    <span>Kaliteli</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Section */}
      <section id="kalite" className="py-24 bg-gradient-to-br from-[#1a1a2e] to-[#16213e] text-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl lg:text-6xl font-bold mb-4">
              Kalite Bizim İşimiz
            </h2>
            <p className="text-xl text-gray-300">
              Avrupa standartlarında malzeme ve teknoloji
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8 mb-16">
            <div className="text-center group cursor-pointer">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-sun-line text-6xl text-[#DC2626] group-hover:scale-110 transition-transform"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">UV Dayanıklı Baskı</h3>
              <p className="text-sm text-gray-400">Güneşe karşı maksimum koruma</p>
            </div>

            <div className="text-center group cursor-pointer">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-shield-check-line text-6xl text-[#DC2626] group-hover:scale-110 transition-transform"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">5 Yıl Garanti</h3>
              <p className="text-sm text-gray-400">Uzun ömürlü kullanım garantisi</p>
            </div>

            <div className="text-center group cursor-pointer">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-rocket-line text-6xl text-[#DC2626] group-hover:scale-110 transition-transform"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">Hızlı Teslimat</h3>
              <p className="text-sm text-gray-400">Zamanında teslim garantisi</p>
            </div>

            <div className="text-center group cursor-pointer">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-tools-line text-6xl text-[#DC2626] group-hover:scale-110 transition-transform"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">Profesyonel Montaj</h3>
              <p className="text-sm text-gray-400">Uzman ekip tarafından kurulum</p>
            </div>

            <div className="text-center group cursor-pointer">
              <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <i className="ri-customer-service-2-line text-6xl text-[#DC2626] group-hover:scale-110 transition-transform"></i>
              </div>
              <h3 className="text-lg font-bold mb-2">7/24 Destek</h3>
              <p className="text-sm text-gray-400">Her zaman yanınızdayız</p>
            </div>
          </div>

          <div className="flex flex-wrap justify-center gap-8 items-center">
            <img 
              src="https://readdy.ai/api/search-image?query=professional%20large%20format%20printing%20machine%20equipment%20close-up%20detail%20shot%2C%20high-tech%20industrial%20printer%20machinery%2C%20modern%20printing%20technology%20with%20orange%20accent%20lights%2C%20clean%20white%20background%2C%20commercial%20grade%20printing%20equipment%2C%20precision%20engineering&width=400&height=300&seq=quality-machine-1&orientation=landscape"
              alt="Baskı Makinesi"
              className="w-64 h-48 object-cover rounded-2xl shadow-2xl hover:scale-105 transition-transform"
            />
            <img 
              src="https://readdy.ai/api/search-image?query=high%20quality%20printing%20materials%20and%20vinyl%20rolls%20in%20vibrant%20colors%2C%20professional%20grade%20banner%20fabric%20samples%2C%20premium%20printing%20substrates%2C%20colorful%20material%20swatches%2C%20clean%20white%20background%2C%20commercial%20printing%20supplies&width=400&height=300&seq=quality-materials-1&orientation=landscape"
              alt="Kaliteli Malzemeler"
              className="w-64 h-48 object-cover rounded-2xl shadow-2xl hover:scale-105 transition-transform"
            />
            <img 
              src="https://readdy.ai/api/search-image?query=professional%20print%20quality%20inspection%20process%2C%20color%20calibration%20and%20quality%20control%20in%20printing%20facility%2C%20technician%20checking%20print%20output%20quality%2C%20precision%20color%20matching%2C%20clean%20white%20background%2C%20professional%20printing%20standards&width=400&height=300&seq=quality-control-1&orientation=landscape"
              alt="Kalite Kontrol"
              className="w-64 h-48 object-cover rounded-2xl shadow-2xl hover:scale-105 transition-transform"
            />
          </div>
        </div>
      </section>

      {/* References Section */}
      <section id="referanslar" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16">
            <div className="inline-block bg-[#DC2626] text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
              Müşteri Yorumları
            </div>
            <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              Müşterilerimiz<br />
              Bizim En Büyük Referansımız
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Review 1 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#DC2626] to-[#EF4444] flex items-center justify-center text-white text-xl font-bold">
                  KR
                </div>
                <div>
                  <div className="font-bold text-lg text-gray-900">Keyf Restaurant</div>
                  <div className="text-sm text-gray-500">@Restoran</div>
                </div>
              </div>
              <p className="text-gray-600 mb-4 leading-relaxed">
                "Restoranımızın cephe brandası için Yıldırım Reklam ile çalıştık. Hem tasarım hem de uygulama mükemmel oldu. 2 yıldır hiçbir sorun yaşamadık, renkler hala ilk günkü gibi canlı!"
              </p>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#DC2626]"></i>
                ))}
              </div>
            </div>

            {/* Review 2 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#DC2626] to-[#EF4444] flex items-center justify-center text-white text-xl font-bold">
                  AY
                </div>
                <div>
                  <div className="font-bold text-lg text-gray-900">Ankara Yapı İnşaat</div>
                  <div className="text-sm text-gray-500">@İnşaat</div>
                </div>
              </div>
              <p className="text-gray-600 mb-4 leading-relaxed">
                "Şantiye çit brandalarımızı yaptırdık. Kaliteli malzeme, hızlı teslimat ve uygun fiyat. Tüm projelerimizde Yıldırım Reklam ile çalışıyoruz artık."
              </p>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#DC2626]"></i>
                ))}
              </div>
            </div>

            {/* Review 3 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#DC2626] to-[#EF4444] flex items-center justify-center text-white text-xl font-bold">
                  MB
                </div>
                <div>
                  <div className="font-bold text-lg text-gray-900">Moda Butik</div>
                  <div className="text-sm text-gray-500">@Perakende</div>
                </div>
              </div>
              <p className="text-gray-600 mb-4 leading-relaxed">
                "Mağazamızın vitrin giydirmesi ve iç mekan baskıları için tercih ettik. Tasarımları çok şık, müşterilerimizden sürekli övgü alıyoruz. Teşekkürler!"
              </p>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#DC2626]"></i>
                ))}
              </div>
            </div>

            {/* Review 4 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#DC2626] to-[#EF4444] flex items-center justify-center text-white text-xl font-bold">
                  OG
                </div>
                <div>
                  <div className="font-bold text-lg text-gray-900">Oto Galeri 34</div>
                  <div className="text-sm text-gray-500">@Otomotiv</div>
                </div>
              </div>
              <p className="text-gray-600 mb-4 leading-relaxed">
                "Galerinin tüm reklam çalışmalarını Yıldırım Reklam yapıyor. Araç giydirme, cephe brandası, tabela... Her işi profesyonelce hallettiler. Kesinlikle tavsiye ederim."
              </p>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#DC2626]"></i>
                ))}
              </div>
            </div>

            {/* Review 5 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#DC2626] to-[#EF4444] flex items-center justify-center text-white text-xl font-bold">
                  EK
                </div>
                <div>
                  <div className="font-bold text-lg text-gray-900">Eğitim Koleji</div>
                  <div className="text-sm text-gray-500">@Eğitim</div>
                </div>
              </div>
              <p className="text-gray-600 mb-4 leading-relaxed">
                "Okulumuzun tanıtım afişleri, pankartlar ve yönlendirme tabelaları için çalıştık. Hem fiyat hem kalite açısından çok memnun kaldık. İletişimleri de çok iyi."
              </p>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#DC2626]"></i>
                ))}
              </div>
            </div>

            {/* Review 6 */}
            <div className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#DC2626] to-[#EF4444] flex items-center justify-center text-white text-xl font-bold">
                  SM
                </div>
                <div>
                  <div className="font-bold text-lg text-gray-900">Sağlık Merkezi Plus</div>
                  <div className="text-sm text-gray-500">@Sağlık</div>
                </div>
              </div>
              <p className="text-gray-600 mb-4 leading-relaxed">
                "Kliniğimizin dış cephe brandası ve iç mekan yönlendirme sistemleri için tercih ettik. Hijyenik malzeme kullanımı ve temiz işçilik için teşekkür ederiz."
              </p>
              <div className="flex gap-1">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-[#DC2626]"></i>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="iletisim" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-gradient-to-br from-[#1a1a2e] to-[#16213e] rounded-[3rem] p-12 lg:p-16">
            <div className="grid lg:grid-cols-3 gap-12">
              {/* Contact Form */}
              <div className="lg:col-span-1">
                <h2 className="text-4xl font-bold text-white mb-4">
                  Hemen İletişime Geçin
                </h2>
                <p className="text-gray-300 mb-8">
                  Projeniz için ücretsiz keşif ve teklif alın
                </p>

                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="Adınız Soyadınız"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="w-full bg-transparent border-b-2 border-white/30 text-white placeholder-gray-400 py-3 focus:border-[#DC2626] focus:outline-none transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Telefon Numaranız"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="w-full bg-transparent border-b-2 border-white/30 text-white placeholder-gray-400 py-3 focus:border-[#DC2626] focus:outline-none transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      placeholder="Mesajınız"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      maxLength={500}
                      rows={4}
                      className="w-full bg-transparent border-b-2 border-white/30 text-white placeholder-gray-400 py-3 focus:border-[#DC2626] focus:outline-none transition-colors resize-none"
                      required
                    ></textarea>
                    <div className="text-gray-400 text-xs mt-1">
                      {formData.message.length}/500 karakter
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={formStatus === 'sending'}
                    className="w-full bg-white text-gray-900 py-4 rounded-full font-semibold hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer disabled:opacity-50"
                  >
                    {formStatus === 'sending' ? 'Gönderiliyor...' : formStatus === 'success' ? 'Gönderildi ✓' : formStatus === 'error' ? 'Hata! Tekrar Deneyin' : 'Gönder →'}
                  </button>
                </form>
              </div>

              {/* Quick Links */}
              <div>
                <h3 className="text-xl font-bold text-white mb-6">Hızlı Linkler</h3>
                <div className="space-y-3">
                  <button onClick={() => scrollToSection('hizmetler')} className="block text-gray-300 hover:text-white transition-colors cursor-pointer">
                    Hizmetler
                  </button>
                  <button onClick={() => scrollToSection('kalite')} className="block text-gray-300 hover:text-white transition-colors cursor-pointer">
                    Kalite
                  </button>
                  <button onClick={() => scrollToSection('referanslar')} className="block text-gray-300 hover:text-white transition-colors cursor-pointer">
                    Referanslar
                  </button>
                  <button onClick={() => scrollToSection('iletisim')} className="block text-gray-300 hover:text-white transition-colors cursor-pointer">
                    İletişim
                  </button>
                </div>
              </div>

              {/* Contact Info */}
              <div>
                <h3 className="text-xl font-bold text-white mb-6">İletişim Bilgileri</h3>
                <div className="space-y-4">
                  <div>
                    <a href="tel:03123808099" className="text-2xl font-bold text-white hover:text-[#DC2626] transition-colors block cursor-pointer">
                      0312 380 80 99
                    </a>
                    <a href="tel:+908503073704" className="text-lg text-gray-300 hover:text-white transition-colors block mt-1 cursor-pointer">
                      +90 850 307 3704
                    </a>
                  </div>
                  <div>
                    <a href="mailto:yildirimdijital@gmail.com" className="text-gray-300 hover:text-white transition-colors cursor-pointer">
                      yildirimdijital@gmail.com
                    </a>
                  </div>
                  <div className="pt-4">
                    <h4 className="text-white font-semibold mb-2">Adres</h4>
                    <p className="text-gray-300 leading-relaxed">
                      Kazım Karabekir Caddesi No:91 D:28,<br />
                      Başkent İş Merkezi, 06070 Altındağ/Ankara
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer Bottom */}
            <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
              <div className="flex gap-6">
                <a href="#" className="text-gray-300 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-2xl"></i>
                </a>
                <a href="#" className="text-gray-300 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-facebook-circle-line text-2xl"></i>
                </a>
                <a href="#" className="text-gray-300 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-linkedin-box-line text-2xl"></i>
                </a>
              </div>

              <div className="text-gray-400 text-sm text-center">
                © 2025 Yıldırım Reklam. Tüm hakları saklıdır.
              </div>

              <div className="flex gap-4 text-sm">
                <a href="https://cdco.com.tr" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors cursor-pointer">
                  BU SİTE CDCO TASARIMDAN YAPILMIŞTIR.
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
